import {
  AssistantMessage,
  ChatTemplate,
  Content,
  DecisionAnswer,
  JSONSchema,
  Message,
  OutputMediaContent,
} from "@/types";
import { Request } from "@/types/requests";
import { getRequestMessages, getResponseMessage } from "@/utils/utils";

export type RequestDisplayData = Request & {
  request_json_fallback?: unknown;
  response_json_fallback?: unknown;
  response_format?: {
    type?: string | null;
  } | null;
  is_embedding?: boolean;
  is_image?: boolean;
  is_audio?: boolean;
  is_video?: boolean;
  is_decision?: boolean;
  decision_answers?: DecisionAnswer[];
};

export type RequestDisplaySource = Request | Partial<RequestDisplayData> | null;

export const getJsonSchemaFromParameters = (
  parameters?: Record<string, unknown>,
): { type: "json_schema"; json_schema: JSONSchema } | undefined => {
  if (!parameters) {
    return undefined;
  }

  const responseFormat = parameters.response_format as
    | { type?: string; json_schema?: JSONSchema }
    | undefined;
  if (responseFormat) {
    return responseFormat as { type: "json_schema"; json_schema: JSONSchema };
  }

  const outputConfig = parameters.output_config as
    | {
        format?: { type?: string; schema?: JSONSchema };
        textFormat?: {
          type?: string;
          structure?: {
            jsonSchema?: { schema?: string | object; name?: string; description?: string };
          };
        };
      }
    | undefined;
  if (outputConfig?.format?.type === "json_schema" && outputConfig.format.schema) {
    return {
      type: "json_schema",
      json_schema: {
        schema: outputConfig.format.schema,
      } as JSONSchema,
    };
  }

  const textFormat = outputConfig?.textFormat;
  if (textFormat?.type === "json_schema" && textFormat.structure?.jsonSchema) {
    const jsonSchemaDef = textFormat.structure.jsonSchema;
    let schema: object;
    const schemaVal = jsonSchemaDef.schema;

    if (typeof schemaVal === "string") {
      try {
        schema = JSON.parse(schemaVal) as object;
      } catch {
        return undefined;
      }
    } else if (schemaVal && typeof schemaVal === "object") {
      schema = schemaVal as object;
    } else {
      return undefined;
    }

    return {
      type: "json_schema",
      json_schema: {
        schema,
        name: jsonSchemaDef.name ?? "schema",
        description: jsonSchemaDef.description ?? "",
      } as JSONSchema,
    };
  }

  return undefined;
};

/** Bedrock Converse stores Claude schemas in ``outputConfig`` and GPT-OSS in ``additionalModelRequestFields``. */
export const getJsonSchemaFromBedrockFunctionKwargs = (
  functionKwargs?: Record<string, unknown>,
): ReturnType<typeof getJsonSchemaFromParameters> => {
  if (!functionKwargs) {
    return undefined;
  }

  const outputConfig = (functionKwargs.outputConfig ?? functionKwargs.output_config) as
    | Record<string, unknown>
    | undefined;
  if (outputConfig) {
    const fromOutputConfig = getJsonSchemaFromParameters({ output_config: outputConfig });
    if (fromOutputConfig) {
      return fromOutputConfig;
    }
  }

  const additionalFields = (functionKwargs.additionalModelRequestFields ??
    functionKwargs.additional_model_request_fields) as Record<string, unknown> | undefined;

  return getJsonSchemaFromParameters({
    response_format: additionalFields?.response_format,
  });
};

export const getJsonSchemaForRequestDisplay = (
  parameters?: Record<string, unknown>,
  functionKwargs?: Record<string, unknown>,
): ReturnType<typeof getJsonSchemaFromParameters> =>
  getJsonSchemaFromParameters(parameters) ?? getJsonSchemaFromBedrockFunctionKwargs(functionKwargs);

export const isMalformedMessage = (message: Message): boolean =>
  Boolean(message.content && !Array.isArray(message.content));

export const isOutputMediaContent = (content: Content): content is OutputMediaContent =>
  content.type === "output_media" && "url" in content && Boolean(content.url);

const collectOutputMedia = (contents?: Content[] | null) =>
  Array.isArray(contents) ? contents.filter(isOutputMediaContent) : [];

export const isGeneratedMediaPlaceholder = (text: string) => {
  const trimmed = text.trim();
  if (/^\[(Video|Image|Audio) Generated\]$/i.test(trimmed)) {
    return true;
  }

  const match = trimmed.match(/^Role:\s*.+\nContent:\s*([\s\S]*)$/i);
  if (!match) {
    return false;
  }

  const content = match[1]?.trim() ?? "";
  return content === "" || /^\[(Video|Image|Audio) Generated\]$/i.test(content);
};

export const getChatRequestDisplayState = (
  chatTemplate: ChatTemplate,
  responseMessageOverride?: AssistantMessage | null,
) => {
  const requestMessages = getRequestMessages(chatTemplate);
  const responseMessage = responseMessageOverride ?? getResponseMessage(chatTemplate);
  const allMessages = responseMessage ? [...requestMessages, responseMessage] : requestMessages;
  const hasResponseContent = Boolean(
    responseMessage &&
      ((responseMessage.content?.length ?? 0) > 0 || (responseMessage.tool_calls?.length ?? 0) > 0),
  );

  return {
    allMessages,
    hasResponseContent,
    requestMessages,
    responseMessage,
  };
};

export const getRequestOutputMedia = (
  requestInfo: RequestDisplayData,
  responseMessageOverride?: AssistantMessage | null,
): OutputMediaContent[] => {
  const template = requestInfo.prompt_version?.prompt_template;
  if (!template) {
    return [];
  }

  if (template.type !== "chat") {
    return collectOutputMedia(template.content as Content[] | undefined);
  }

  const { responseMessage } = getChatRequestDisplayState(template, responseMessageOverride);
  return collectOutputMedia(responseMessage?.content);
};
